import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Brain, ArrowsClockwise, PaperPlaneRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

const FIELDS = [
  ["client", "Client"],
  ["value", "Valoare / comercial"],
  ["deadlines", "Termene cheie"],
  ["problems", "Probleme / riscuri"],
];

export default function ProjectMemory({ project, reload }) {
  const s = project.ai_project_summary || null;
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/ai-summary`);
      toast.success("Memorie proiect actualizată de AI");
      reload && reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    const history = messages.slice(-6);
    setMessages((m) => [...m, { role: "user", content: question }]);
    setQ(""); setAsking(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/ask`, { question, history });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Eroare: nu am putut răspunde acum." }]);
    } finally { setAsking(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="project-memory-panel">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-head text-lg font-bold"><Brain size={20} className="text-primary" /> Memorie proiect (AI)</h3>
        <Button size="sm" onClick={generate} disabled={busy} data-testid="generate-memory-btn">
          <ArrowsClockwise size={15} className="mr-1.5" /> {busy ? "Se analizează…" : s ? "Actualizează" : "Generează rezumat"}
        </Button>
      </div>
      {!s ? (
        <p className="text-sm text-muted-foreground">Apasă „Generează rezumat" — AI-ul citește toată corespondența proiectului și extrage client, valoare, termene și probleme.</p>
      ) : (
        <div className="space-y-4" data-testid="project-memory-content">
          {s.summary && <p className="text-sm leading-relaxed">{s.summary}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="rounded-lg border border-border p-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className="mt-1 text-sm">{s[k] || "—"}</div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Din {s.emails_count || 0} emailuri · generat {fmtDate(s.generated_at)}
          </div>
        </div>
      )}

      <div className="mt-6 border-t border-border pt-5" data-testid="project-chat">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold"><PaperPlaneRight size={16} className="text-primary" /> Întreabă proiectul</h4>
        <div className="mb-3 max-h-72 space-y-2 overflow-y-auto">
          {messages.length === 0 && <p className="text-xs text-muted-foreground">Ex: „când s-a livrat?", „ce probleme au fost cu clientul?", „ce termene au fost depășite?"</p>}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : "text-left"} data-testid={`chat-msg-${i}`}>
              <span className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-primary/20 text-foreground" : "bg-white/5 text-foreground/90"}`}>{m.content}</span>
            </div>
          ))}
          {asking && <div className="text-left text-xs text-muted-foreground">Se gândește…</div>}
        </div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Scrie o întrebare despre proiect…" data-testid="chat-input" />
          <Button onClick={ask} disabled={asking || !q.trim()} data-testid="chat-send"><PaperPlaneRight size={16} /></Button>
        </div>
      </div>
    </div>
  );
}
